// ═══════════════════════════════════════════════════════════════
// Impact — Bulk Entry Form
// Port from BulkEntry.jsx — Firebase → Ignatius via stores
// Creates multiple assessments at once by damage category counts
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, Select, Button, NumberInput,
  Group, Stack, Divider, Alert, LoadingOverlay, Progress, Modal,
  Table, Badge, ThemeIcon,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useImpactStore } from '../../store/impactStore';
import type { Assessment } from '../../store/impactStore';
import LocationInput, { type LocationValue } from './components/LocationInput';
import DamageTypeSelector from './components/DamageTypeSelector';
import PhotoCapture from './components/PhotoCapture';
import { primary } from '../../lib/colors';

// ── Category config ────────────────────────────────────────

const DAMAGE_CATEGORIES = [
  { key: 'affected', label: 'Affected', color: '#16a34a', icon: 'fa-house' },
  { key: 'minor', label: 'Minor', color: '#d97706', icon: 'fa-house-crack' },
  { key: 'major', label: 'Major', color: '#ea580c', icon: 'fa-house-chimney-crack' },
  { key: 'destroyed', label: 'Destroyed', color: '#dc2626', icon: 'fa-house-fire' },
  { key: 'inaccessible', label: 'Inaccessible', color: '#7c3aed', icon: 'fa-road-barrier' },
];

const DWELLING_TYPES = [
  { value: 'single_family', label: 'Single Family Home' },
  { value: 'apartment', label: 'Apartment / Condo' },
  { value: 'mobile_home', label: 'Mobile Home / Manufactured' },
  { value: 'townhouse', label: 'Townhouse / Duplex' },
  { value: 'multi_family', label: 'Multi-Family (3+)' },
  { value: 'other', label: 'Other' },
];

// ── Component ──────────────────────────────────────────────

export default function BulkEntry() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { bulkCreateAssessments } = useImpactStore();

  // Category counts
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    Object.fromEntries(DAMAGE_CATEGORIES.map(c => [c.key, 0]))
  );
  // Shared fields
  const [damageTypes, setDamageTypes] = useState<string[]>([]);
  const [dwellingType, setDwellingType] = useState('');
  const [neighborhoodName, setNeighborhoodName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState<LocationValue>({});
  const [photos, setPhotos] = useState<File[]>([]);

  // Progress
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Derived ────────────────────────────────────────────

  const categoryBreakdown = useMemo(
    () => DAMAGE_CATEGORIES
      .filter(c => (categoryCounts[c.key] || 0) > 0)
      .map(c => ({ ...c, count: categoryCounts[c.key] })),
    [categoryCounts]
  );

  const totalHomes = useMemo(
    () => Object.values(categoryCounts).reduce((sum, n) => sum + (n || 0), 0),
    [categoryCounts]
  );

  // ── Guard ──────────────────────────────────────────────

  if (!activeIncident) {
    return (
      <Alert color="yellow" title="No Active Incident">
        <Text>Select an active incident before creating assessments.</Text>
      </Alert>
    );
  }

  // ── Handlers ───────────────────────────────────────────

  const updateCount = (key: string, value: number) => {
    setCategoryCounts(prev => ({ ...prev, [key]: Math.max(0, value || 0) }));
  };

  const handleGpsFromPhoto = (gps: { lat: number; lng: number }) => {
    if (!location.lat) {
      setLocation(prev => ({ ...prev, lat: gps.lat, lng: gps.lng }));
    }
  };

  const handleConfirm = () => {
    if (totalHomes === 0) {
      notifications.show({ title: 'Required', message: 'Enter at least one home count.', color: 'red' });
      return;
    }
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setProgress(0);

    try {
      // Build one assessment per category with count > 0
      const assessments: Partial<Assessment>[] = categoryBreakdown.map(cat => ({
        incidentId: String(activeIncident.id),
        assessmentType: 'individual',
        damageCategory: cat.key,
        homeCount: Number(cat.count) || 1,
        damageTypes,
        dwellingType: dwellingType || '',
        address: location.address || '',
        county: location.county || '',
        fipsCode: location.fipsCode || '',
        stateCode: location.state || '',
        latitude: location.lat || null,
        longitude: location.lng || null,
        neighborhood: neighborhoodName.trim(),
        description: description || '',
        method: 'bulk',
        bulkEntry: true,
        assessorName: user?.fullName || '',
        assessorEmail: user?.email || '',
      }));

      // Track progress as each assessment is created
      let completed = 0;
      for (const assessment of assessments) {
        await bulkCreateAssessments([assessment]);
        completed++;
        setProgress(Math.round((completed / assessments.length) * 100));
      }

      // NOTE: Photo upload skipped for bulk entries.
      // The original shared photo URLs across bulk records, which doesn't map
      // cleanly to per-assessment photo records in the Ignatius data model.

      notifications.show({
        title: 'Bulk Entry Complete',
        message: `Created ${assessments.length} assessment(s) covering ${totalHomes} homes.`,
        color: 'green',
      });
      navigate('/impact/assessments');
    } catch (err) {
      console.error('Bulk submit failed:', err);
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create bulk assessments.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  };

  // ── Render ─────────────────────────────────────────────

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={submitting} />

      <Stack gap="lg">
        <div>
          <Title order={2}>
            <i className="fa-solid fa-clipboard-list" style={{ marginRight: 10, color: primary[700] }} />
            Bulk Damage Entry
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            Quickly log multiple homes by damage category for a neighborhood or area
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Incident: <Text span fw={600}>{activeIncident.name}</Text>
          </Text>
        </div>

        <Divider />

        {/* Category Counts */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Homes by Damage Category</Text>
            <Text size="xs" c="dimmed">
              Enter the number of homes in each FEMA damage category
            </Text>

            {DAMAGE_CATEGORIES.map(cat => (
              <Group key={cat.key} gap="sm" align="center">
                <ThemeIcon
                  variant="light"
                  size="lg"
                  radius="md"
                  style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                >
                  <i className={`fa-solid ${cat.icon}`} style={{ fontSize: 14 }} />
                </ThemeIcon>
                <Text fw={500} size="sm" style={{ flex: 1 }}>{cat.label}</Text>
                <NumberInput
                  value={categoryCounts[cat.key] || ''}
                  onChange={(val) => updateCount(cat.key, Number(val) || 0)}
                  min={0}
                  max={9999}
                  w={100}
                  size="md"
                  placeholder="0"
                />
              </Group>
            ))}

            {totalHomes > 0 && (
              <Group justify="flex-end" mt="xs">
                <Badge size="lg" variant="filled" color="blue">
                  Total: {totalHomes} home{totalHomes !== 1 ? 's' : ''}
                </Badge>
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Shared Info */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Area / Neighborhood</Text>
            <TextInput
              label="Neighborhood Name"
              placeholder="e.g., Riverside Estates, Block 400"
              value={neighborhoodName}
              onChange={(e) => setNeighborhoodName(e.target.value)}
              size="md"
            />
            <Select
              label="Dwelling Type"
              placeholder="Select primary dwelling type"
              value={dwellingType || null}
              onChange={(val) => setDwellingType(val || '')}
              data={DWELLING_TYPES}
              size="md"
            />
          </Stack>
        </Paper>

        {/* Damage Types */}
        <Paper withBorder p="md" radius="md">
          <DamageTypeSelector
            value={damageTypes}
            onChange={setDamageTypes}
            mode="ia"
          />
        </Paper>

        {/* Description */}
        <Paper withBorder p="md" radius="md">
          <Textarea
            label="Description"
            placeholder="General description of damage in this area..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minRows={3}
            autosize
            size="md"
          />
        </Paper>

        {/* Location */}
        <Paper withBorder p="md" radius="md">
          <LocationInput
            value={location}
            onChange={setLocation}
          />
        </Paper>

        {/* Photos — display/capture only, not uploaded for bulk entries */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Reference Photos</Text>
            <Text size="xs" c="dimmed">
              Photos captured here are for reference only and will not be attached to individual records.
            </Text>
            <PhotoCapture
              photos={photos}
              onChange={setPhotos}
              onGpsExtracted={handleGpsFromPhoto}
              maxPhotos={10}
            />
          </Stack>
        </Paper>

        {/* Progress Bar */}
        {submitting && progress > 0 && (
          <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Creating assessments...</Text>
              <Progress value={progress} size="lg" animated striped />
              <Text size="xs" c="dimmed" ta="center">{progress}%</Text>
            </Stack>
          </Paper>
        )}

        <Divider />

        {/* Submit */}
        <Group justify="space-between">
          <Button variant="subtle" onClick={() => navigate('/impact/assessments')}>
            Cancel
          </Button>
          <Button
            size="lg"
            loading={submitting}
            onClick={handleConfirm}
            leftSection={<i className="fa-solid fa-paper-plane" />}
            disabled={totalHomes === 0}
          >
            Submit {totalHomes > 0 ? `${totalHomes} Home${totalHomes !== 1 ? 's' : ''}` : 'Bulk Entry'}
          </Button>
        </Group>
      </Stack>

      {/* Confirmation Modal */}
      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Bulk Entry"
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            You are about to create <Text span fw={700}>{categoryBreakdown.length} assessment record{categoryBreakdown.length !== 1 ? 's' : ''}</Text> covering <Text span fw={700}>{totalHomes} home{totalHomes !== 1 ? 's' : ''}</Text>.
          </Text>

          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Category</Table.Th>
                <Table.Th ta="right">Homes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {categoryBreakdown.map(cat => (
                <Table.Tr key={cat.key}>
                  <Table.Td>
                    <Group gap="xs">
                      <i className={`fa-solid ${cat.icon}`} style={{ color: cat.color, fontSize: 13 }} />
                      <Text size="sm">{cat.label}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text fw={600}>{cat.count}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td fw={700}>Total</Table.Td>
                <Table.Td ta="right" fw={700}>{totalHomes}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          {location.address && (
            <Text size="xs" c="dimmed">
              <i className="fa-solid fa-location-dot" style={{ marginRight: 6 }} />
              {location.address}
            </Text>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setConfirmOpen(false)}>
              Go Back
            </Button>
            <Button onClick={handleSubmit} color="green">
              Confirm &amp; Submit
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
